import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Stack,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import RuleIcon from '@mui/icons-material/Rule';
import { attendanceWebApi } from './attendanceWebApi';
import { TABLE_PAPER_SX, HEAD_CELL_SX, BODY_CELL_SX, TABLE_ROW_SX } from '../../common/tableStyles';

const CARD_SX = {
  p: { xs: 2, md: 3 },
  borderRadius: '20px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};

function formatSeconds(secs) {
  if (secs == null) return '—';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}h ${m}m`;
}

function formatTime(isoStr) {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return isoStr;
  }
}

function formatDate(isoStr) {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return isoStr;
  }
}

export default function WebAttendanceHistory({ refreshKey }) {
  const [activeTab, setActiveTab] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [regularizations, setRegularizations] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [histRes, regRes] = await Promise.all([
        attendanceWebApi.history(),
        attendanceWebApi.regularizations(),
      ]);
      setSessions(histRes?.results || []);
      setRegularizations(regRes?.results || []);
    } catch (err) {
      console.error('Failed to load web attendance history:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  return (
    <Paper elevation={0} sx={{ ...CARD_SX, mb: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, minHeight: 48 },
            '& .Mui-selected': { color: '#2f6bff !important' },
            '& .MuiTabs-indicator': { bgcolor: '#2f6bff' },
          }}
        >
          <Tab icon={<HistoryIcon />} iconPosition="start" label="Past Web Sessions" />
          <Tab icon={<RuleIcon />} iconPosition="start" label="Regularization Requests" />
        </Tabs>
      </Stack>

      {loading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <CircularProgress size={30} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Loading records…
          </Typography>
        </Box>
      ) : activeTab === 0 ? (
        sessions.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No web attendance sessions recorded yet.
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={TABLE_PAPER_SX}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={HEAD_CELL_SX}>Date</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>Clock In</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>Clock Out</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>Net Duration</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>Break</TableCell>
                  <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((s) => {
                  const isClosed = Boolean(s.closed_at);
                  return (
                    <TableRow key={s.id} hover sx={TABLE_ROW_SX}>
                      <TableCell sx={BODY_CELL_SX}>
                        <Typography variant="body2" fontWeight={600}>
                          {s.attendance_date || formatDate(s.opened_at)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>{formatTime(s.opened_at)}</TableCell>
                      <TableCell sx={BODY_CELL_SX}>{formatTime(s.closed_at)}</TableCell>
                      <TableCell sx={BODY_CELL_SX}>{formatSeconds(s.duration_excluding_breaks_seconds)}</TableCell>
                      <TableCell sx={BODY_CELL_SX}>{formatSeconds(s.completed_break_seconds)}</TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        {isClosed ? (
                          <Chip label="Completed" size="small" sx={{ bgcolor: '#F3F4F6', color: '#4B5563', fontWeight: 600 }} />
                        ) : (
                          <Chip label="Open" size="small" sx={{ bgcolor: '#ECFDF5', color: '#059669', fontWeight: 700 }} />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : regularizations.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No regularization requests submitted yet.
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={TABLE_PAPER_SX}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={HEAD_CELL_SX}>Requested At</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Event</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Reason</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Status</TableCell>
                <TableCell sx={HEAD_CELL_SX}>Review Comment</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {regularizations.map((r) => {
                const colorMap = {
                  PENDING: { bg: '#FEF3C7', fg: '#D97706' },
                  APPROVED: { bg: '#ECFDF5', fg: '#059669' },
                  REJECTED: { bg: '#FEE2E2', fg: '#DC2626' },
                };
                const c = colorMap[r.status] || { bg: '#F3F4F6', fg: '#6B7280' };
                return (
                  <TableRow key={r.id} hover sx={TABLE_ROW_SX}>
                    <TableCell sx={BODY_CELL_SX}>
                      <Typography variant="body2" fontWeight={600}>
                        {formatDate(r.requested_at)} {formatTime(r.requested_at)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={BODY_CELL_SX}>
                      <Chip label={r.event_type.replace('_', ' ')} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ ...BODY_CELL_SX, maxWidth: 260 }}>
                      <Typography variant="body2" noWrap title={r.reason}>
                        {r.reason}
                      </Typography>
                    </TableCell>
                    <TableCell sx={BODY_CELL_SX}>
                      <Chip label={r.status} size="small" sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 700 }} />
                    </TableCell>
                    <TableCell sx={BODY_CELL_SX}>
                      <Typography variant="body2" color="text.secondary">
                        {r.review_comment || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
